import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { useFlyoutClose } from 'components/modular-nav/NavItem'
import { navItemWrapperXPaddingPx, navItemWrapperYPaddingPx } from 'components/modular-nav/types'
import { useRecentItems } from 'hooks/recent-items'
import { RecentItemsCategory } from 'lib/constants/recent-items'
import { Route } from 'lib/routes'
import { Pin } from 'lucide-react'
import { useRouter } from 'next/router'
import { ReactNode, useMemo } from 'react'
import { useIntl } from 'react-intl'

// Styling constants
const ITEM_HEIGHT = 36

type RecentItemsListProps<TRef> = {
    /** Title to display above the list */
    title: ReactNode
    /** Category for pin/unpin operations */
    category: RecentItemsCategory
    /** Convert a storage key back to a ref */
    parseRef: (key: string) => TRef
    /** Get the link route for a ref */
    getLink: (ref: TRef) => Route
    /** Render function for each item - receives the ref and renders the item content */
    renderItem: (ref: TRef) => ReactNode
    /** Maximum items to show (pinned items count towards this limit) */
    maxItems?: number
}

/**
 * Generic component for displaying a list of recent items with pin/unpin functionality.
 * Each item handles its own data fetching via the renderItem function.
 *
 * @template TRef - The ref type (e.g., string for simple IDs, or an object for composite refs)
 */
export function RecentItemsList<TRef>({
    title,
    category,
    parseRef,
    getLink,
    renderItem,
    maxItems = 10,
}: RecentItemsListProps<TRef>): ReactNode {
    const intl = useIntl()
    const router = useRouter()
    const closeFlyout = useFlyoutClose()
    const {
        items,
        isLoading,
        pinItem,
        unpinItem,
        isPinned,
    } = useRecentItems(category, { maxItems })

    // Parse refs from storage keys
    const parsedItems = useMemo(
        () => items.map((entry) => ({
            key: entry.id,
            ref: parseRef(entry.id),
            isPinned: entry.isPinned,
        })),
        [items, parseRef],
    )

    if (items.length === 0 && !isLoading) {
        return null
    }

    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ p: 1 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontWeight: 500, pl: (t) => `calc(${navItemWrapperXPaddingPx}px + ${t.spacing(1)})` }}
                >
                    {title}
                </Typography>
            </Box>

            {isLoading && items.length === 0 ? (
                <Stack spacing={1} sx={{ px: 1 }}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} height={ITEM_HEIGHT} sx={{ borderRadius: 1 }} />
                    ))}
                </Stack>
            ) : (
                <Stack spacing={0}>
                    {parsedItems.map((item) => {
                        const pinned = isPinned(item.key)
                        const itemPath = getLink(item.ref)
                        const isActive = router.asPath.startsWith(itemPath) || router.pathname.startsWith(itemPath)

                        return (
                            <Box
                                key={item.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: `${navItemWrapperYPaddingPx}px ${navItemWrapperXPaddingPx}px`,

                                    '&:hover': {
                                        '& .interior-hover': {
                                            backgroundColor: 'primary.extraLight',
                                        },
                                    },
                                    '&:focus-within, &:hover': {
                                        '& .pin-button': {
                                            opacity: 1,
                                        },
                                    },
                                }}
                            >
                                {/* Item content - wrapped in link */}
                                <Stack
                                    direction="row"
                                    gap={1}
                                    className="interior-hover"
                                    sx={{
                                        borderRadius: 1,
                                        width: '100%',
                                        backgroundColor: isActive ? 'primary.extraLight' : 'transparent',
                                    }}
                                >
                                    <Box
                                        component={C1Link}
                                        href={itemPath}
                                        onClick={closeFlyout ?? undefined}
                                        sx={{
                                            flex: 1,
                                            overflow: 'hidden',
                                            p: 1,
                                            pl: 1,
                                            color: isActive ? 'primary.main' : 'inherit',
                                            fontWeight: isActive ? 600 : 'inherit',
                                        }}
                                    >
                                        {renderItem(item.ref)}
                                    </Box>

                                    {/* Pin/Unpin button */}
                                    <IconButton
                                        className="pin-button"
                                        size="small"
                                        onClick={(e): void => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            if (pinned) {
                                                unpinItem(item.key)
                                            } else {
                                                pinItem(item.key)
                                            }
                                        }}
                                        aria-label={pinned
                                            ? intl.formatMessage({ id: 'recent_items_unpin', defaultMessage: 'Unpin' })
                                            : intl.formatMessage({ id: 'recent_items_pin', defaultMessage: 'Pin' })}
                                        sx={{
                                            opacity: pinned ? 1 : 0,
                                            transition: 'opacity 0.15s',
                                            ml: 1,
                                            p: 1,
                                            backgroundColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                opacity: 1,
                                                '& .pin-icon': {
                                                    color: 'primary.main',
                                                },
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent',
                                                opacity: 1,
                                                outline: '2px solid',
                                                outlineColor: 'primary.main',
                                                outlineOffset: -2,
                                                borderRadius: 1,
                                            },
                                        }}
                                    >
                                        <C1Icon
                                            icon={Pin}
                                            className="pin-icon"
                                            size="sm"
                                            sx={{ color: pinned ? 'primary.main' : 'text.secondary' }}
                                        />
                                    </IconButton>
                                </Stack>
                            </Box>
                        )
                    })}
                </Stack>
            )}
        </Box>
    )
}
